import { BLUE_600, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { Stack, Typography } from '@mui/material'

import { FC, KeyboardEvent } from 'react'
import { MUICheckbox } from 'components/common/MUICheckBox'
import { MUIRadio } from 'components/common/MUIRadio'

/** @typedef CheckButtonType Available check button type */
type CheckButtonType = 'check' | 'radio'


/**
 * @interface Props CheckboxItem properties
 */
export interface Props {
  /** Type of check button */
  type?: CheckButtonType,
  /** Title of checkbox item */
  title: string,
  /** Description of checkbox item */
  description?: string,
  /** Whether the checkbox is checked */
  checked: boolean,
  /** Function to run when the checkbox is clicked */
  onClick: () => void
}

/**
 * @component
 * A checkbox (or radio) row whose whole line is the control: it takes focus and toggles on click,
 * Enter or Space.
 *
 * @example
 * <CheckboxItem title="Attic" description="Include the attic" checked={hasAttic} onClick={toggleAttic} />
 */
export const CheckboxItem: FC<Props> = ({
  type = 'check',
  title,
  description,
  checked,
  onClick
}) => {
  const Control = type === 'radio' ? MUIRadio : MUICheckbox

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Enter' && e.key !== ' ') return
    e.preventDefault()
    onClick()
  }

  return (
    // The text reads as part of the control: without `userSelect: none` it shows a caret and highlights on
    // click, which reads as an editable field.
    <Stack
      flexDirection="row"
      gap=".8rem"
      alignItems="flex-start"
      role={type === 'radio' ? 'radio' : 'checkbox'}
      aria-checked={checked}
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      sx={{ cursor: 'pointer', userSelect: 'none', outline: 'none', borderRadius: '.4rem', '&:focus-visible': { boxShadow: `0 0 0 2px ${BLUE_600}` } }}
    >

      {/* The row owns the role and the keyboard; the control only shows the state. Cancelling its click keeps
          a click on it from reaching the row twice through its own label. */}
      <Control
        checked={checked}
        tabIndex={-1}
        inputProps={{ tabIndex: -1, 'aria-hidden': true }}
        onClick={(e) => e.preventDefault()}
        sx={{ padding: '.5rem 0' }}
      />

      <Stack>
        <Typography variant="text-md" color={GRAY_900}>{title}</Typography>

        {description &&
          <Typography variant="text-sm" color={GRAY_700}>{description}</Typography>
        }
      </Stack>
    </Stack>
  )
}
